import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, FileSpreadsheet, DollarSign, FileText, BookOpen, Calendar, User, Users } from 'lucide-react';
import { TimeRangeSelector } from './TimeRangeSelector';
import { SummaryCards } from './SummaryCards';
import { PipelineProgressionChart } from './PipelineProgressionChart';
import { CostPerStage } from './CostPerStage';
import { OverallDeliverability } from './OverallDeliverability';
import { BillingButton } from '../BillingButton';
import { TeammateSelector, type Teammate, type ExtraItem } from '../TeammateSelector';
import { TeamManagementModal } from '../TeamManagementModal';
import NotificationBell from '../NotificationBell';
import { fetchWithAuthRecovery } from '../../lib/fetch-with-auth-recovery';

// ============================================================================
// Types
// ============================================================================

interface DashboardPageProps {
  locationId: string;
  onNavigateToScrubber?: () => void;
  onNavigateToWallet?: () => void;
  billingMode?: 'ghl' | 'wallet' | null;
  billingBalance?: number | null;
  billingLoading?: boolean;
  hasActivePlan?: boolean;
  isInternal?: boolean;
}

interface SummaryMetrics {
  incoming_messages: number;
  outgoing_messages: number;
  outgoing_segments: number;
  opt_out_count: number;
  failed_count: number;
  spam_filtered_count: number;
}

interface SummaryData {
  range: SummaryMetrics;
  today: SummaryMetrics;
  lifetime: SummaryMetrics;
}

interface PipelineProgressionItem {
  state: string;
  label: string;
  color: string;
  count: number;
}

interface CpaPeriodData {
  totalCost: number;
  acquisitions: number;
  quotes: number;
  bookings: number;
  appointments: number;
  costPerAcquisition: number | null;
  costPerQuote: number | null;
  costPerBooking: number | null;
  costPerAppointment: number | null;
}

interface CpaData {
  period: CpaPeriodData;
  lifetime: CpaPeriodData;
}

interface DeliverabilityPoint {
  time_bucket: string;
  sent: number;
  delivered: number;
}

interface DeliverabilityData {
  trend: DeliverabilityPoint[];
  totals: {
    sent: number;
    delivered: number;
    deliveryRate: number;
  };
  granularity: string;
}

// API Response Types
interface SummaryResponse {
  success: boolean;
  summary?: SummaryData;
}

interface PipelineProgressionResponse {
  success: boolean;
  progression?: PipelineProgressionItem[];
  totalEvents?: number;
}

interface CpaResponse {
  success: boolean;
  period?: CpaData['period'];
  lifetime?: CpaData['lifetime'];
}

interface DeliverabilityResponse {
  success: boolean;
  trend?: DeliverabilityPoint[];
  totals?: DeliverabilityData['totals'];
  granularity?: string;
}

// ============================================================================
// Constants
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL;

// Auto-refresh interval for "today" views (45 seconds)
const AUTO_REFRESH_INTERVAL = 45000;

// ============================================================================
// Dashboard scope (Phase D)
// ============================================================================

/**
 * Which location(s) the dashboard is showing data for.
 *  - 'mine'           → caller's own location (default; existing endpoints, no extra params)
 *  - 'teammate'       → a single teammate's data (existing endpoints with ?as=)
 *  - 'team'           → aggregated across all teammates (new /team/* endpoints, include_self=false)
 *  - 'team_plus_self' → aggregated across all teammates + caller (include_self=true)
 *
 * Never persisted; defaults to 'mine' on every page load.
 */
type DashboardScope =
  | { kind: 'mine' }
  | { kind: 'teammate'; teammate: Teammate }
  | { kind: 'team' }
  | { kind: 'team_plus_self' };

/**
 * Build a dashboard fetch URL that honors the current scope.
 *
 * Routing:
 *   mine           → /dashboard/:locationId/:endpoint?start=&end=
 *   teammate       → /dashboard/:locationId/:endpoint?start=&end=&as=<teammate_loc>
 *   team           → /dashboard/:locationId/team/:endpoint?start=&end=&include_self=false
 *   team_plus_self → /dashboard/:locationId/team/:endpoint?start=&end=&include_self=true
 *
 * The four supported endpoints — summary, pipeline-progression, cpa,
 * deliverability — exist in both single-location and team-aggregate
 * forms.
 */
function buildDashboardUrl(
  endpoint: 'summary' | 'pipeline-progression' | 'cpa' | 'deliverability',
  locationId: string,
  scope: DashboardScope,
  start: string,
  end: string
): string {
  const params = new URLSearchParams({ start, end });
  switch (scope.kind) {
    case 'mine':
      return `${API_URL}/dashboard/${locationId}/${endpoint}?${params}`;
    case 'teammate':
      params.set('as', scope.teammate.location_id);
      return `${API_URL}/dashboard/${locationId}/${endpoint}?${params}`;
    case 'team':
      params.set('include_self', 'false');
      return `${API_URL}/dashboard/${locationId}/team/${endpoint}?${params}`;
    case 'team_plus_self':
      params.set('include_self', 'true');
      return `${API_URL}/dashboard/${locationId}/team/${endpoint}?${params}`;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

// Format a Date as a local YYYY-MM-DD string for backend params.
// Avoids toISOString() which would shift the date by a day in non-UTC zones
// (e.g. local end-of-day in US Eastern crosses midnight UTC and becomes "tomorrow").
const toLocalDateString = (d: Date): string => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Get default date range (last 14 days)
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 14);
  return { start, end };
};

// Check if date range includes today
const includestoday = (endDate: Date) => {
  const today = new Date();
  return endDate.toDateString() === today.toDateString();
};

// ============================================================================
// Dashboard Page Component
// ============================================================================

export function DashboardPage({
  locationId,
  onNavigateToScrubber,
  onNavigateToWallet,
  billingMode,
  billingBalance,
  billingLoading,
  hasActivePlan = true,
  isInternal = false,
}: DashboardPageProps) {
  // Date range state
  const [startDate, setStartDate] = useState<Date>(() => getDefaultDateRange().start);
  const [endDate, setEndDate] = useState<Date>(() => getDefaultDateRange().end);

  // Data states
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [pipelineProgression, setPipelineProgression] = useState<PipelineProgressionItem[]>([]);
  const [cpaData, setCpaData] = useState<CpaData | null>(null);
  const [deliverabilityData, setDeliverabilityData] = useState<DeliverabilityData | null>(null);

  // Loading states
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingPipeline, setLoadingPipeline] = useState(true);
  const [loadingCpa, setLoadingCpa] = useState(true);
  const [loadingDeliverability, setLoadingDeliverability] = useState(true);

  // Last updated timestamp
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Auto-refresh ref
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ============================================================================
  // Scope / team state (Phase D)
  // ============================================================================

  // What the dashboard is showing data for. Defaults to caller's own
  // location; never persisted, resets to 'mine' on every page load.
  const [scope, setScope] = useState<DashboardScope>({ kind: 'mine' });

  // The caller's teammates (people they've added via team_members). Loaded
  // once on mount; refreshed whenever the TeamManagementModal commits a
  // change. Used to (a) populate the selector's teammate options and (b)
  // count "N teammates" / "N+1 locations" in the aggregate extra labels.
  const [teammates, setTeammates] = useState<Teammate[]>([]);

  // Adjust Team Members modal — opened from the selector's footer.
  const [teamModalOpen, setTeamModalOpen] = useState(false);

  // Fetch teammates on mount. Silent on failure (non-blocking — empty list
  // just means the selector shows only "My Stats" and the Adjust footer).
  useEffect(() => {
    let cancelled = false;
    fetchWithAuthRecovery(`${API_URL}/billing/${locationId}/team-members`, {
      credentials: 'include',
    })
      .then((r) => r.json() as Promise<{ success: boolean; team_members?: Teammate[] }>)
      .then((data) => {
        if (!cancelled && data.success && data.team_members) {
          setTeammates(data.team_members);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [locationId]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchSummary = useCallback(async (silent = false) => {
    if (!silent) setLoadingSummary(true);
    try {
      const url = buildDashboardUrl(
        'summary',
        locationId,
        scope,
        toLocalDateString(startDate),
        toLocalDateString(endDate)
      );
      const response = await fetchWithAuthRecovery(url, {
        credentials: 'include',
      });
      const data: SummaryResponse = await response.json();
      if (data.success && data.summary) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      if (!silent) setLoadingSummary(false);
    }
  }, [locationId, startDate, endDate, scope]);

  const fetchPipelineProgression = useCallback(async (silent = false) => {
    if (!silent) setLoadingPipeline(true);
    try {
      const url = buildDashboardUrl(
        'pipeline-progression',
        locationId,
        scope,
        toLocalDateString(startDate),
        toLocalDateString(endDate)
      );
      const response = await fetchWithAuthRecovery(url, {
        credentials: 'include',
      });
      const data: PipelineProgressionResponse = await response.json();
      if (data.success) {
        setPipelineProgression(data.progression || []);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline progression:', error);
    } finally {
      if (!silent) setLoadingPipeline(false);
    }
  }, [locationId, startDate, endDate, scope]);

  const fetchCpa = useCallback(async (silent = false) => {
    if (!silent) setLoadingCpa(true);
    try {
      const url = buildDashboardUrl(
        'cpa',
        locationId,
        scope,
        toLocalDateString(startDate),
        toLocalDateString(endDate)
      );
      const response = await fetchWithAuthRecovery(url, {
        credentials: 'include',
      });
      const data: CpaResponse = await response.json();
      if (data.success && data.period && data.lifetime) {
        setCpaData({
          period: data.period,
          lifetime: data.lifetime,
        });
      }
    } catch (error) {
      console.error('Failed to fetch CPA:', error);
    } finally {
      if (!silent) setLoadingCpa(false);
    }
  }, [locationId, startDate, endDate, scope]);

  const fetchDeliverability = useCallback(async (silent = false) => {
    if (!silent) setLoadingDeliverability(true);
    try {
      const url = buildDashboardUrl(
        'deliverability',
        locationId,
        scope,
        toLocalDateString(startDate),
        toLocalDateString(endDate)
      );
      const response = await fetchWithAuthRecovery(url, {
        credentials: 'include',
      });
      const data: DeliverabilityResponse = await response.json();
      if (data.success && data.trend && data.totals) {
        setDeliverabilityData({
          trend: data.trend,
          totals: data.totals,
          granularity: data.granularity || 'daily',
        });
      }
    } catch (error) {
      console.error('Failed to fetch deliverability:', error);
    } finally {
      if (!silent) setLoadingDeliverability(false);
    }
  }, [locationId, startDate, endDate, scope]);

  // Fetch all data
  const fetchAllData = useCallback(async (silent = false) => {
    await Promise.all([
      fetchSummary(silent),
      fetchPipelineProgression(silent),
      fetchCpa(silent),
      fetchDeliverability(silent),
    ]);
    setLastUpdated(new Date());
  }, [fetchSummary, fetchPipelineProgression, fetchCpa, fetchDeliverability]);

  // Initial data load
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh for "today" views
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Set up auto-refresh if viewing today
    if (includestoday(endDate)) {
      refreshIntervalRef.current = setInterval(() => {
        fetchAllData(true); // silent refresh
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [endDate, fetchAllData]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleDateRangeChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleManualRefresh = () => {
    fetchAllData();
  };

  // ============================================================================
  // Scope handlers
  // ============================================================================

  // User picked a teammate from the selector.
  const handleTeammateSelect = (t: Teammate | null) => {
    if (t) setScope({ kind: 'teammate', teammate: t });
  };

  // User picked one of the extras (My Stats / Team Summary / Team + My Stats).
  const handleExtraSelect = (id: string) => {
    if (id === 'mine') setScope({ kind: 'mine' });
    else if (id === 'team') setScope({ kind: 'team' });
    else if (id === 'team_plus_self') setScope({ kind: 'team_plus_self' });
  };

  // TeamManagementModal callback: commit list changes and fix scope if the
  // currently-selected teammate just got removed, or if the team became
  // empty while a team-aggregate scope was active.
  const handleTeammatesChange = (next: Teammate[]) => {
    setTeammates(next);
    if (
      (scope.kind === 'teammate' && !next.find((t) => t.id === scope.teammate.id)) ||
      ((scope.kind === 'team' || scope.kind === 'team_plus_self') && next.length === 0)
    ) {
      setScope({ kind: 'mine' });
    }
  };

  // Build the selector's extras list. "My Stats" always shows; the two
  // team-aggregate items only appear when there is at least one teammate.
  const teamCount = teammates.length;
  const selectorExtras: ExtraItem[] = [
    {
      id: 'mine',
      label: 'My Stats',
      icon: <User className="w-4 h-4" />,
      position: 'top',
    },
    ...(teamCount > 0
      ? ([
          {
            id: 'team',
            label: `Team Summary (${teamCount} teammate${teamCount === 1 ? '' : 's'})`,
            icon: <Users className="w-4 h-4" />,
            position: 'bottom',
            separatorBefore: true,
          },
          {
            id: 'team_plus_self',
            label: `Team + My Stats (${teamCount + 1} locations)`,
            icon: <Users className="w-4 h-4" />,
            position: 'bottom',
          },
        ] as ExtraItem[])
      : []),
  ];

  // Translate the discriminated scope into the controlled-value props
  // TeammateSelector expects (a Teammate for teammate scope, an extra id
  // otherwise).
  const selectorValue = scope.kind === 'teammate' ? scope.teammate : null;
  const selectorExtraId = scope.kind !== 'teammate' ? scope.kind : null;

  // ============================================================================
  // Render
  // ============================================================================

  const defaultSummary: SummaryData = {
    range: { incoming_messages: 0, outgoing_messages: 0, outgoing_segments: 0, opt_out_count: 0, failed_count: 0, spam_filtered_count: 0 },
    today: { incoming_messages: 0, outgoing_messages: 0, outgoing_segments: 0, opt_out_count: 0, failed_count: 0, spam_filtered_count: 0 },
    lifetime: { incoming_messages: 0, outgoing_messages: 0, outgoing_segments: 0, opt_out_count: 0, failed_count: 0, spam_filtered_count: 0 },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-8">
          {/* Row 1: Title and Navigation Buttons */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">SMS Analytics & Performance</p>
            </div>

            <div className="flex items-center gap-3">
              {/* Notification bell — leftmost so it reads as status, not
                  navigation. Inert (no badge, empty history) unless a
                  NotificationsProvider is mounted above this page. */}
              <NotificationBell />
              {/* Navigation buttons for wallet mode users */}
              {onNavigateToScrubber && (
                <button
                  onClick={onNavigateToScrubber}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  <span className="text-sm">List Scrubber</span>
                </button>
              )}
              {billingMode === 'wallet' && onNavigateToWallet && (
                <BillingButton
                  onClick={onNavigateToWallet}
                  balance={billingBalance ?? null}
                  loading={billingLoading ?? false}
                />
              )}
            </div>
          </div>

          {/* Row 1.5: Scope selector (below subtitle, prominent).
              Defaults to "My Stats" and never persists — refresh resets it.
              Teammate / Team Summary / Team + My Stats are picked here;
              all dashboard fetches re-fire on scope change via useCallback deps. */}
          <div className="mt-3 flex items-center gap-2 max-w-md">
            <span className="text-sm font-medium text-gray-700 flex-shrink-0">Viewing:</span>
            <div className="flex-1">
              <TeammateSelector
                value={selectorValue}
                onChange={handleTeammateSelect}
                teammates={teammates}
                extras={selectorExtras}
                selectedExtraId={selectorExtraId}
                onExtraSelect={handleExtraSelect}
                onAdjust={() => setTeamModalOpen(true)}
                placeholder="My Stats"
              />
            </div>
          </div>

          {/* Row 2: Date Controls */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <TimeRangeSelector
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
              />
              <span className="text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
            <button
              onClick={handleManualRefresh}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            All reports are filtered using date ranges in UTC, not local time. Daily cut-off in your timezone is{' '}
            {new Date(new Date().setUTCHours(0, 0, 0, 0)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}.
          </p>
        </header>

        {/* Main Content - hidden when wallet mode + no plan (modal is forced open), unless internal user */}
        {billingMode === 'wallet' && !hasActivePlan && !isInternal ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <p>Please select a plan to continue</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Row 1: Summary Cards */}
            <SummaryCards
              rangeData={summary?.range || defaultSummary.range}
              todayData={summary?.today || defaultSummary.today}
              lifetimeData={summary?.lifetime || defaultSummary.lifetime}
              loading={loadingSummary}
            />

            {/* Row 2: Pipeline Progression and Overall Deliverability */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[360px]">
              <PipelineProgressionChart
                data={pipelineProgression}
                loading={loadingPipeline}
                title="Pipeline Progression"
              />
              <OverallDeliverability
                data={deliverabilityData}
                loading={loadingDeliverability}
                title="Overall Deliverability"
              />
            </div>

            {/* Row 3: Cost Per Stage (funnel order: Acquisition → Quote → Booking → Appointment) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <CostPerStage
                title="Cost Per Acquisition"
                icon={DollarSign}
                iconBgClass="bg-blue-50"
                iconColorClass="text-blue-600"
                countLabel="acquisitions"
                helpText="Cost per contact who responded positively to the intro message (Collecting Info stage)."
                periodCpa={cpaData?.period.costPerAcquisition ?? null}
                periodCount={cpaData?.period.acquisitions ?? 0}
                lifetimeCpa={cpaData?.lifetime.costPerAcquisition ?? null}
                lifetimeCount={cpaData?.lifetime.acquisitions ?? 0}
                loading={loadingCpa}
              />
              <CostPerStage
                title="Cost Per Quote"
                icon={FileText}
                iconBgClass="bg-purple-50"
                iconColorClass="text-purple-600"
                countLabel="quotes"
                helpText="Cost per contact at the Quote Pending stage."
                periodCpa={cpaData?.period.costPerQuote ?? null}
                periodCount={cpaData?.period.quotes ?? 0}
                lifetimeCpa={cpaData?.lifetime.costPerQuote ?? null}
                lifetimeCount={cpaData?.lifetime.quotes ?? 0}
                loading={loadingCpa}
              />
              <CostPerStage
                title="Cost Per Booking"
                icon={BookOpen}
                iconBgClass="bg-amber-50"
                iconColorClass="text-amber-600"
                countLabel="bookings"
                helpText="Cost per contact at the Booking stage."
                periodCpa={cpaData?.period.costPerBooking ?? null}
                periodCount={cpaData?.period.bookings ?? 0}
                lifetimeCpa={cpaData?.lifetime.costPerBooking ?? null}
                lifetimeCount={cpaData?.lifetime.bookings ?? 0}
                loading={loadingCpa}
              />
              <CostPerStage
                title="Cost Per Appointment"
                icon={Calendar}
                iconBgClass="bg-green-50"
                iconColorClass="text-green-600"
                countLabel="appointments"
                helpText="Cost per booked appointment. Lower is better."
                periodCpa={cpaData?.period.costPerAppointment ?? null}
                periodCount={cpaData?.period.appointments ?? 0}
                lifetimeCpa={cpaData?.lifetime.costPerAppointment ?? null}
                lifetimeCount={cpaData?.lifetime.appointments ?? 0}
                loading={loadingCpa}
              />
            </div>
          </div>
        )}
      </div>

      {/* Team management modal triggered from the scope selector's Adjust footer. */}
      <TeamManagementModal
        isOpen={teamModalOpen}
        onClose={() => setTeamModalOpen(false)}
        locationId={locationId}
        teammates={teammates}
        onTeammatesChange={handleTeammatesChange}
      />
    </div>
  );
}
